import { Deserializer, makeQueryStringSerializer } from 'core/lib/serializer';
import { omit } from 'lodash';
import * as qs from 'query-string';

import apiFetch from './apiFetch';
import { API_URL } from './constants';
import * as Types from './definitions';
import * as Enums from './enums';

const defaultRequest: Partial<Types.PromotionRequest> = {
    page: 1,
    states: [
        Enums.PromotionState.Active,
        Enums.PromotionState.Draft,
        Enums.PromotionState.Pending,
        Enums.PromotionState.Ready,
    ],
    origin: 'Start',
};
const serializer = makeQueryStringSerializer<Types.PromotionRequest>(defaultRequest, {
    page: Deserializer.Int,
    sortDescending: Deserializer.Bool,
});

export default class Promotion {
    static defaultRequest = defaultRequest;

    static serializeRequest = serializer.serialize;

    static deserializeRequest = serializer.deserialize;

    static async fetchAll(
        clientId: string,
        programId: string,
        request: Partial<Types.PromotionRequest>,
    ): Promise<Types.PromotionResult> {
        const query = serializer.serialize(request, true);
        const url = `${API_URL}/api/v1/client/${clientId}/program/${programId}/promotion?${query}`;
        const response = await apiFetch(url);
        const json = await response.json();
        return json;
    }

    static async fetch(clientId: string, programId: string, promotionId: string): Promise<Types.Promotion | undefined> {
        const response = await apiFetch(
            `${API_URL}/api/v1/client/${clientId}/program/${programId}/Promotion/${promotionId}`,
        );
        if (response.status === 404) {
            return;
        }
        const promotion: Types.Promotion = await response.json();
        return promotion;
    }

    static async fetchSummaries(
        clientId: string,
        programId: string,
        states?: Enums.PromotionState[],
    ): Promise<Types.PromotionSummary[]> {
        const query = states ? qs.stringify({ states }, { arrayFormat: 'index' }) : '';
        const url = `${API_URL}/api/v1/client/${clientId}/program/${programId}/promotion/summaries?${query}`;
        const response = await apiFetch(url);
        const json = await response.json();
        return json;
    }

    static async fetchProfileFields(clientId: string, programId: string): Promise<Types.ProfileField[]> {
        const url = `${API_URL}/api/v1/client/${clientId}/program/${programId}/promotion/eligibility/profilefields`;
        const response = await apiFetch(url);
        const json = await response.json();
        return json;
    }

    static async fetchProfileFieldValues(
        clientId: string,
        programId: string,
        request: Types.ProfileFieldValuesSearchRequest,
    ): Promise<string[]> {
        const query = qs.stringify(request);
        const url = `${API_URL}/api/v1/client/${clientId}/program/${programId}/promotion/eligibility/profilefields/values?${query}`;
        const response = await apiFetch(url);
        const json = await response.json();
        return json;
    }

    static async updateEligibilityGroup(
        clientId: string,
        programId: string,
        promotionId: string,
        eligibilityGroup: Types.EligibilityGroup,
    ): Promise<Types.EligibilityGroup> {
        const sanitizedEligibilityGroupFields = eligibilityGroup.eligibilityGroupFields.map((egf) => {
            if (egf.id < 0) {
                return omit(egf, 'id') as Types.EligibilityGroupProfileField;
            }
            return egf;
        });
        const sanitizedEligibilityGroup: Types.EligibilityGroup = {
            ...eligibilityGroup,
            eligibilityGroupFields: sanitizedEligibilityGroupFields,
        };
        const response = await apiFetch(
            `${API_URL}/api/v1/client/${clientId}/program/${programId}/Promotion/${promotionId}/eligibility`,
            {
                headers: [['Content-Type', 'application/json']],
                method: 'PUT',
                body: JSON.stringify(sanitizedEligibilityGroup),
            },
        );
        const updatedEligibilityGroup: Types.EligibilityGroup = await response.json();
        return updatedEligibilityGroup;
    }

    static async create(clientId: string, newPromotion: Types.Promotion): Promise<string> {
        const promotion = omit(newPromotion, 'id');
        const response = await apiFetch(
            `${API_URL}/api/v1/client/${clientId}/program/${promotion.programId}/Promotion`,
            {
                headers: [['Content-Type', 'application/json']],
                method: 'POST',
                body: JSON.stringify(promotion),
            },
        );
        const newPromotionId: string = await response.json();
        return newPromotionId;
    }

    static async update(clientId: string, updates: Types.Promotion) {
        const updatesSanitized = {
            ...updates,
            questions: updates.questions.map((q) => {
                if (q && q.id === '') {
                    return omit(q, 'id', 'dateAdded') as Types.PromotionQuestion;
                }
                return q;
            }),
        };

        return await apiFetch(`${API_URL}/api/v1/client/${clientId}/program/${updates.programId}/Promotion`, {
            headers: [['Content-Type', 'application/json']],
            method: 'PUT',
            body: JSON.stringify(updatesSanitized),
        });
    }

    static async close(clientId: string, programId: string, promotionId: string | undefined) {
        return await apiFetch(
            `${API_URL}/api/v1/client/${clientId}/program/${programId}/Promotion/ClosePromotion/${promotionId}`,
            {
                method: 'PUT',
            },
        );
    }
}
